import { DISPLAY_CONTENT } from 'modules/globalSms/constants';
import { useQuerySmsPreferencesCountryVersionHistory as useQueryHistory } from 'modules/globalSms/hooks';
import { EditablePreferenceFields, OptOutType } from 'modules/globalSms/types';
import { getCharacterCountText } from 'modules/globalSms/utils';
import { useController } from 'react-hook-form';
import { LeftTooltip } from 'ui/tooltip';
import { Undo } from 'ui/icons';
import { spacing } from 'ui/theme';
import { OptOutTextArea, RestrictionText, StyledFormField, UndoButton } from './styles';

const {
  alphanumericFieldHelpText,
  alphanumericFieldTitle,
  numericFieldHelpText,
  numericFieldTitle,
  undoBtnTooltipContent,
} = DISPLAY_CONTENT.editModal;

type Props = { optOutType: OptOutType; regionCode: string };

export const OptOutTextFormField = ({ optOutType, regionCode, ...props }: Props) => {
  const fieldName = `${optOutType}OptOut` as keyof EditablePreferenceFields;
  const { field } = useController<EditablePreferenceFields>({ name: fieldName });

  const { results: countryVersionHistory } = useQueryHistory(regionCode);
  const defaultVersion = countryVersionHistory.find((version) => !version.createdAt);
  const defaultFieldValue = defaultVersion?.[fieldName];
  const isFieldNonDefault = field.value !== defaultFieldValue;

  const handleChange = (value: string) => field.onChange(value);

  const getContentText = (type: OptOutType) => {
    switch (type) {
      case OptOutType.ALPHANUMERIC:
        return { helpText: alphanumericFieldHelpText, labelText: alphanumericFieldTitle };
      case OptOutType.NUMERIC:
        return { helpText: numericFieldHelpText, labelText: numericFieldTitle };
      default:
        return undefined;
    }
  };

  const restrictionText = (
    <RestrictionText>
      {getCharacterCountText(field.value)}
      {isFieldNonDefault && defaultFieldValue && (
        <LeftTooltip tooltipContent={undoBtnTooltipContent}>
          <UndoButton onClick={() => handleChange(defaultFieldValue)}>
            <Undo />
          </UndoButton>
        </LeftTooltip>
      )}
    </RestrictionText>
  );

  return (
    <StyledFormField
      data-test={`edit-preferences-modal-form-field-${optOutType}`}
      helpText={getContentText(optOutType)?.helpText}
      labelText={getContentText(optOutType)?.labelText}
      restrictionText={restrictionText}
      {...props}
    >
      <OptOutTextArea
        data-test={`opt-out-text-input-${optOutType}`}
        autoGrowMinHeight={spacing.double}
        onChange={(value: string) => handleChange(value)}
        value={field.value}
      />
    </StyledFormField>
  );
};
